import { useEffect, useState } from "react";
import { Download, Loader2, Lock, LogOut, Users, Megaphone, Mail, Heart } from "lucide-react";
import { API, api } from "@/lib/api";

const TOKEN_KEY = "mhg_admin_token";

const DATASETS = [
  { key: "signups", label: "Community & Giver Signups", icon: Users, testid: "export-signups" },
  { key: "causes", label: "Cause Submissions", icon: Megaphone, testid: "export-causes" },
  { key: "contacts", label: "Contact Messages", icon: Mail, testid: "export-contacts" },
  { key: "subscribers", label: "Opt-in Subscribers", icon: Heart, testid: "export-subscribers" },
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    if (!token) return;
    api
      .get("/admin/stats", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setStats(null);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { password });
      sessionStorage.setItem(TOKEN_KEY, r.data.access_token);
      setToken(r.data.access_token);
      setPassword("");
    } catch {
      setError("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  const exportCsv = async (key) => {
    setDownloading(key);
    try {
      const res = await fetch(`${API}/admin/export/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        logout();
        setError("Session expired; sign in again");
        return;
      }
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myheartgives-${key}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed. Please try again.");
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-14 sm:px-6 lg:pt-20" data-testid="admin-page">
      {!token ? (
        <div className="rounded-3xl border border-line bg-white p-8 shadow-sm sm:p-10" data-testid="admin-login-card">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-crimson-soft text-crimson">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-5 font-serif text-2xl font-bold text-ink sm:text-3xl">Admin sign in</h1>
          <p className="mt-2 text-sm text-cocoa">
            Shared password only — for the MyHeartGives team.
          </p>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Shared admin password"
              aria-label="Shared admin password"
              className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold"
              data-testid="admin-password-input"
            />
            {error && (
              <p className="text-sm font-medium text-crimson" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3 text-sm font-semibold text-ivory transition-colors duration-200 hover:bg-crimson-deep disabled:opacity-60"
              data-testid="admin-login-submit-button"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      ) : (
        <div data-testid="admin-dashboard">
          <div className="flex items-center justify-between">
            <h1 className="font-serif text-2xl font-bold text-ink sm:text-3xl">Community Dashboard</h1>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-medium text-cocoa transition-colors hover:border-crimson hover:text-crimson"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>

          {stats && (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="admin-stats">
              {[
                ["Signups", stats.signups, "stat-signups"],
                ["Causes", stats.causes, "stat-causes"],
                ["Messages", stats.contacts, "stat-contacts"],
                ["Opt-ins", stats.subscribers, "stat-subscribers"],
              ].map(([label, value, testid]) => (
                <div key={label} className="rounded-2xl border border-line bg-white p-5 text-center" data-testid={testid}>
                  <div className="font-serif text-3xl font-bold text-crimson">{value}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-taupe">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2 className="mt-12 font-serif text-xl font-bold text-ink">Export submissions</h2>
          <div className="mt-5 space-y-4">
            {DATASETS.map((d) => (
              <div
                key={d.key}
                className="flex items-center justify-between rounded-2xl border border-line bg-white p-5"
                data-testid={`${d.testid}-row`}
              >
                <div className="flex items-center gap-3">
                  <d.icon className="h-5 w-5 text-gold" />
                  <span className="text-sm font-medium text-ink">{d.label}</span>
                </div>
                <button
                  onClick={() => exportCsv(d.key)}
                  disabled={downloading === d.key}
                  className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-xs font-semibold text-ivory transition-colors hover:bg-crimson-deep disabled:opacity-60"
                  data-testid={`${d.testid}-button`}
                >
                  {downloading === d.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  CSV
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
